type AudioData = Blob | ArrayBuffer;

interface RecordingResult {
  data: Blob | null;
  duration: number; // 录音时长（秒）
}

interface AudioRecorderOptions {
  sharedAudioContext?: AudioContext;
}

export type AudioRecorderType = {
  start: () => Promise<void>;
  stop: () => Promise<RecordingResult>;
  getCurrentDuration: () => number;
  isRecording: boolean;
};

class AudioRecorder implements AudioRecorderType {
  public isRecording: boolean = false;

  private mediaRecorder: MediaRecorder | null = null;
  private currentStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private audioBuffers: Float32Array[] = [];

  private stopResolve: ((result: RecordingResult) => void) | null = null;
  private stopReject: ((reason?: any) => void) | null = null;

  private startTime: number = 0; // 录音开始时间戳
  private duration: number = 0; // 录音时长（秒）

  private readonly supportsMediaRecorder: boolean;

  constructor(options: AudioRecorderOptions = {}) {
    this.supportsMediaRecorder = this.checkMediaRecorderSupport();

    if (options.sharedAudioContext) {
      this.audioContext = options.sharedAudioContext;
    }
  }

  static async hasMicrophone(): Promise<boolean> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some((device) => device.kind === 'audioinput');
    } catch (e) {
      console.error('检测麦克风失败:', e);
      return false;
    }
  }

  /**
   * 检查浏览器是否支持 MediaRecorder
   */
  private checkMediaRecorderSupport(): boolean {
    try {
      return (
        typeof MediaRecorder !== 'undefined' &&
        (MediaRecorder.isTypeSupported('audio/webm') ||
          MediaRecorder.isTypeSupported('audio/wav'))
      );
    } catch (e) {
      return false;
    }
  }

  /**
   * 清理所有资源
   */
  private cleanup(): void {
    try {
      // 停止 MediaRecorder
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
        this.mediaRecorder = null;
      }

      // 停止 AudioContext 录音
      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }

      // 释放媒体流
      if (this.currentStream) {
        this.currentStream.getTracks().forEach((track) => track.stop());
        this.currentStream = null;
      }

      // 清理回调
      if (this.stopReject) {
        this.stopReject(new Error('录音被取消'));
        this.stopReject = null;
      }

      this.isRecording = false;
      this.audioBuffers = [];
      this.duration = 0;
      this.stopResolve = null;
    } catch (e) {
      console.error('清理资源时出错:', e);
    }
  }

  /**
   * 设置 MediaRecorder 录音
   */
  private setupMediaRecorder(stream: MediaStream): void {
    console.log('setupMediaRecorder');
    const audioChunks: Blob[] = [];

    this.mediaRecorder = new MediaRecorder(stream);

    this.mediaRecorder.addEventListener('dataavailable', (event) => {
      audioChunks.push(event.data);
    });

    this.mediaRecorder.addEventListener('stop', () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
      this.stopResolve?.({ data: audioBlob, duration: this.duration });
      this.cleanup();
    });

    this.mediaRecorder.addEventListener('error', (event) => {
      this.stopReject?.((event as any).error);
      this.cleanup();
    });

    try {
      this.mediaRecorder.start();
    } catch (err) {
      this.cleanup();
      this.stopReject?.(err);
    }
  }

  /**
   * 设置 AudioContext 录音
   */
  private setupAudioContextRecorder(stream: MediaStream): void {
    console.log('setupAudioContextRecorder');
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
      }

      const source = this.audioContext.createMediaStreamSource(stream);
      this.scriptProcessor = this.audioContext.createScriptProcessor(
        4096,
        1,
        1,
      );
      this.audioBuffers = [];

      this.scriptProcessor.onaudioprocess = (event) => {
        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        this.audioBuffers.push(new Float32Array(inputData));
      };

      source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);
    } catch (err) {
      this.cleanup();
      this.stopReject?.(err);
    }
  }

  /**
   * 处理 AudioContext 录音结果
   */
  private processAudioContextRecording(): Blob {
    // 合并缓冲区
    const totalLength = this.audioBuffers.reduce(
      (sum, buffer) => sum + buffer.length,
      0,
    );
    const finalBuffer = new Float32Array(totalLength);

    let offset = 0;
    for (const buffer of this.audioBuffers) {
      finalBuffer.set(buffer, offset);
      offset += buffer.length;
    }

    // 生成 WAV 文件
    const audioBuffer = this.float32ToWavBuffer(
      finalBuffer,
      this.audioContext!.sampleRate,
    );
    const blob = new Blob([audioBuffer], { type: 'audio/wav' });
    return blob;
  }

  /**
   * 将 Float32Array 转换为 WAV 格式
   */
  private float32ToWavBuffer(
    audioData: Float32Array,
    sampleRate: number,
  ): ArrayBuffer {
    const buffer = new ArrayBuffer(44 + audioData.length * 2);
    const view = new DataView(buffer);

    // WAV 文件头
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + audioData.length * 2, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM 格式
    view.setUint16(22, 1, true); // 单声道
    view.setUint32(24, sampleRate, true); // 采样率
    view.setUint32(28, sampleRate * 2, true); // 字节率
    view.setUint16(32, 2, true); // 块对齐
    view.setUint16(34, 16, true); // 位深度
    this.writeString(view, 36, 'data');
    view.setUint32(40, audioData.length * 2, true);

    // 写入音频数据（转换为16位）
    let index = 44;
    for (let i = 0; i < audioData.length; i++) {
      const sample = Math.max(-1, Math.min(1, audioData[i]));
      view.setInt16(
        index,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true,
      );
      index += 2;
    }

    return buffer;
  }

  /**
   * 辅助函数：写入字符串到 DataView
   */
  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  /**
   * 开始录音
   * @returns 开始成功或失败
   */
  start(): Promise<void> {
    this.cleanup(); // 清理任何之前的录音

    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          this.currentStream = stream;
          this.isRecording = true;
          this.startTime = performance.now(); // 记录开始时间

          // 优先使用 MediaRecorder
          if (this.supportsMediaRecorder) {
            this.setupMediaRecorder(stream);
          }
          // 降级使用 AudioContext
          else {
            this.setupAudioContextRecorder(stream);
          }

          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  /**
   * 停止录音并获取结果
   * @returns 录音结果（包含数据和时长）
   */
  stop(): Promise<RecordingResult> {
    if (!this.isRecording) {
      return Promise.resolve({ data: null, duration: 0 });
    }

    // 计算录音时长
    this.duration = (performance.now() - this.startTime) / 1000;

    return new Promise((resolve, reject) => {
      this.stopResolve = resolve;
      this.stopReject = reject;

      try {
        // 停止 MediaRecorder
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
          this.mediaRecorder.stop();
        }
        // 停止 AudioContext 录音并处理结果
        else if (this.scriptProcessor) {
          const wavBuffer = this.processAudioContextRecording();
          resolve({ data: wavBuffer, duration: this.duration });
          this.cleanup();
        }
      } catch (e) {
        reject(e);
        this.cleanup();
      }
    });
  }

  /**
   * 获取当前录音时长（秒）
   */
  getCurrentDuration(): number {
    if (!this.isRecording) return 0;
    return (performance.now() - this.startTime) / 1000;
  }
}

export default AudioRecorder;
